import { pick } from "lodash";
import moment from "moment";
import { z } from "zod";

import {
  assertISODateFormat,
  parseISODateFormat,
  shiftISODate,
  isISODateFormat,
  yesterdayLocalAsISODate,
  todayLocalAsISODate,
} from "../dates";

import {
  enumeratedLiterals,
  type EnumeratedLiteralType,
  type EnumeratedLiteralSubsetType,
} from "./enumeratedLiterals";
import { TimeGranularities, type TimeGranularity } from "./time-granularity";

export const DateRanges = enumeratedLiterals(
  [
    "today",
    "todayAndYesterday",
    "yesterday",
    "last3Days",
    "last7Days",
    "last14Days",
    "last28Days",
    "last30Days",
    "last60Days",
    "thisWeek",
    "lastWeek",
    "thisWeekAndLastWeek",
    "last4Weeks",
    "last12Weeks",
    "last26Weeks",
    "last52Weeks",
    "thisMonth",
    "thisMonthAndLastMonth",
    "yearToDate",
    "last3Months",
    "last6Months",
    "last9Months",
    "last12Months",
    "last18Months",
    "lastYear",
    "last3Years",
    "last5Years",
    "monthToDate",
    "lastMonth",
  ] as const,
  {
    getLabels: (accessors) =>
      ({
        [accessors.TODAY]: "Today",
        [accessors.TODAYANDYESTERDAY]: "Today and Yesterday",
        [accessors.YESTERDAY]: "Yesterday",
        [accessors.LAST3DAYS]: "Last 3 days",
        [accessors.LAST7DAYS]: "Last 7 days",
        [accessors.LAST14DAYS]: "Last 14 days",
        [accessors.LAST28DAYS]: "Last 28 days",
        [accessors.LAST30DAYS]: "Last 30 days",
        [accessors.LAST60DAYS]: "Last 60 days",
        [accessors.MONTHTODATE]: "Month to date",
        [accessors.LASTMONTH]: "Last month",
        [accessors.THISWEEK]: "This week",
        [accessors.LASTWEEK]: "Last week",
        [accessors.THISWEEKANDLASTWEEK]: "This week and last week",
        [accessors.LAST4WEEKS]: "Last month",
        [accessors.LAST12WEEKS]: "Last 3 months",
        [accessors.LAST26WEEKS]: "Last 6 months",
        [accessors.LAST52WEEKS]: "Last year",
        [accessors.THISMONTH]: "This month",
        [accessors.THISMONTHANDLASTMONTH]: "This month and last month",
        [accessors.YEARTODATE]: "Year to date",
        [accessors.LAST3MONTHS]: "Last 3 months",
        [accessors.LAST6MONTHS]: "Last 6 months",
        [accessors.LAST9MONTHS]: "Last 9 months",
        [accessors.LAST12MONTHS]: "Last 12 months",
        [accessors.LAST18MONTHS]: "Last 18 months",
        [accessors.LASTYEAR]: "Last year",
        [accessors.LAST3YEARS]: "Last 3 years",
        [accessors.LAST5YEARS]: "Last 5 years",
      } as const),
    getSubsets: (accessors) =>
      ({
        [`data-export-${TimeGranularities.HOURLY}`]: [
          accessors.TODAY,
          accessors.TODAYANDYESTERDAY,
          accessors.YESTERDAY,
          accessors.LAST3DAYS,
          accessors.LAST7DAYS,
        ],
        [`data-export-${TimeGranularities.DAILY}`]: [
          accessors.TODAY,
          accessors.TODAYANDYESTERDAY,
          accessors.YESTERDAY,
          accessors.LAST3DAYS,
          accessors.LAST7DAYS,
          accessors.LAST14DAYS,
          accessors.LAST30DAYS,
          accessors.LAST60DAYS,
          accessors.LAST6MONTHS,
          accessors.LASTYEAR,
          accessors.MONTHTODATE,
          accessors.LASTMONTH,
        ],
        [TimeGranularities.HOURLY]: [
          accessors.TODAY,
          accessors.YESTERDAY,
          accessors.LAST3DAYS,
          accessors.LAST7DAYS,
        ],
        [TimeGranularities.DAILY]: [
          accessors.TODAY,
          accessors.YESTERDAY,
          accessors.LAST3DAYS,
          accessors.LAST7DAYS,
          accessors.LAST14DAYS,
          accessors.LAST30DAYS,
          accessors.LAST60DAYS,
          accessors.LAST6MONTHS,
          accessors.LASTYEAR,
          accessors.MONTHTODATE,
          accessors.LASTMONTH,
        ],
        [TimeGranularities.WEEKLY]: [
          accessors.THISWEEK,
          accessors.LASTWEEK,
          accessors.LAST4WEEKS,
          accessors.LAST12WEEKS,
          accessors.LAST26WEEKS,
          accessors.LAST52WEEKS,
        ],
        [TimeGranularities.MONTHLY]: [
          accessors.YEARTODATE,
          accessors.LAST6MONTHS,
          accessors.LASTYEAR,
          accessors.LAST3YEARS,
          accessors.LAST5YEARS,
        ],
      } as const),
  },
);

export type DateRange<T extends TimeGranularity | undefined = undefined> =
  T extends TimeGranularity
    ? EnumeratedLiteralSubsetType<T, typeof DateRanges>
    : EnumeratedLiteralType<typeof DateRanges>;

export type DateInterval = {
  startDate: string;
  endDate: string;
};

export type MonthInterval = {
  startMonth: string;
  endMonth: string;
};

export type FixedDateInterval = z.infer<typeof FixedDateRangeSchema>;

export const FixedDateRangeSchema = z
  .object({
    type: z.literal("fixed"),
    startDate: z.string().refine((val) => isISODateFormat(val), {
      message: "The value is not a valid ISO date format.",
    }),
    endDate: z.string().refine((val) => isISODateFormat(val), {
      message: "The value is not a valid ISO date format.",
    }),
  })
  .superRefine((val, ctx) => {
    const startDateMoment = parseISODateFormat(val.startDate);
    if (!startDateMoment.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message: "The start date cannot be converted to a valid moment.",
        fatal: true,
      });
      return z.NEVER;
    }
    const endDateMoment = parseISODateFormat(val.endDate);
    if (!endDateMoment.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message: "The start date cannot be converted to a valid moment.",
        fatal: true,
      });
      return z.NEVER;
    }
    if (startDateMoment.isAfter(endDateMoment)) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message: "The start date must not be after the end date.",
        fatal: true,
      });
      return z.NEVER;
    }
  });

export interface RelativeDateInterval {
  type: "relative";
  windowStartDays: number;
  windowEndDays: number;
}

export type TypedDateInterval = FixedDateInterval | RelativeDateInterval;

export function isFixedDateInterval(date: unknown): date is FixedDateInterval {
  const result = FixedDateRangeSchema.safeParse(date);
  return result.success;
}

export function parseDateInterval(
  dateRange: TypedDateInterval,
  anchor: string,
): DateInterval {
  assertISODateFormat(anchor);

  if (dateRange.type === "fixed") {
    return pick(dateRange, "endDate", "startDate");
  }

  return {
    startDate: shiftISODate(anchor, -dateRange.windowEndDays),
    endDate: shiftISODate(anchor, -dateRange.windowStartDays),
  };
}

const relative = <O extends Omit<RelativeDateInterval, "type">>(
  data: O,
): O & { readonly type: "relative" } => ({ ...data, type: "relative" });

const fixed = <O extends Omit<FixedDateInterval, "type">>(
  data: O,
): O & { readonly type: "fixed" } => ({ ...data, type: "fixed" });

const getStartOfWeek = () => {
  const todayAnchor = todayLocalAsISODate();
  const startOfWeek = moment(todayAnchor, "YYYY-MM-DD").startOf("week");
  const isTodayStartOfWeek = startOfWeek.isSame(todayAnchor, "day");
  if (isTodayStartOfWeek) {
    return startOfWeek;
  }

  const yesterdayAnchor = yesterdayLocalAsISODate();
  return moment(yesterdayAnchor, "YYYY-MM-DD").startOf("week");
};

const getStartOfMonth = () => {
  const todayAnchor = todayLocalAsISODate();
  const startOfMonth = moment(todayAnchor, "YYYY-MM-DD").startOf("month");
  const isTodayStartOfMonth = startOfMonth.isSame(todayAnchor, "day");
  if (isTodayStartOfMonth) {
    return startOfMonth;
  }

  const yesterdayAnchor = yesterdayLocalAsISODate();
  return moment(yesterdayAnchor, "YYYY-MM-DD").startOf("month");
};

const getStartOfYear = () => {
  const todayAnchor = todayLocalAsISODate();
  const startOfYear = moment(todayAnchor, "YYYY-MM-DD").startOf("year");
  const isTodayStartOfYear = startOfYear.isSame(todayAnchor, "day");
  if (isTodayStartOfYear) {
    return startOfYear;
  }

  const yesterdayAnchor = yesterdayLocalAsISODate();
  return moment(yesterdayAnchor, "YYYY-MM-DD").startOf("year");
};

const DateRangeIntervalConversions: {
  [key in DateRange | "yesterdayAndToday"]:
    | TypedDateInterval
    | (() => TypedDateInterval);
} = {
  [DateRanges.TODAY]: relative({
    windowStartDays: 0,
    windowEndDays: 0,
  }),
  [DateRanges.YESTERDAY]: relative({
    windowStartDays: 1,
    windowEndDays: 1,
  }),
  [DateRanges.TODAYANDYESTERDAY]: relative({
    windowStartDays: 0,
    windowEndDays: 1,
  }),
  yesterdayAndToday: relative({
    windowStartDays: 0,
    windowEndDays: 1,
  }),
  [DateRanges.LAST3DAYS]: relative({
    windowStartDays: 1,
    windowEndDays: 3,
  }),
  [DateRanges.LAST7DAYS]: relative({
    windowStartDays: 1,
    windowEndDays: 7,
  }),
  [DateRanges.LAST14DAYS]: relative({
    windowStartDays: 1,
    windowEndDays: 14,
  }),
  [DateRanges.LAST28DAYS]: relative({
    windowStartDays: 1,
    windowEndDays: 28,
  }),
  [DateRanges.LAST30DAYS]: relative({
    windowStartDays: 1,
    windowEndDays: 30,
  }),
  [DateRanges.LAST60DAYS]: relative({
    windowStartDays: 1,
    windowEndDays: 60,
  }),
  [DateRanges.LAST4WEEKS]: () =>
    fixed({
      startDate: getStartOfWeek().subtract(4, "w").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST12WEEKS]: () =>
    fixed({
      startDate: getStartOfWeek().subtract(12, "w").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST26WEEKS]: () =>
    fixed({
      startDate: getStartOfWeek().subtract(26, "w").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST52WEEKS]: () =>
    fixed({
      startDate: getStartOfWeek().subtract(52, "w").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST3MONTHS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(2, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST6MONTHS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(5, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST9MONTHS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(8, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST12MONTHS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(11, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST18MONTHS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(17, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.THISWEEK]: () =>
    fixed({
      startDate: getStartOfWeek().format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LASTWEEK]: () =>
    fixed({
      endDate: getStartOfWeek()
        .subtract(1, "week")
        .endOf("week")
        .format("YYYY-MM-DD"),
      startDate: getStartOfWeek().subtract(1, "week").format("YYYY-MM-DD"),
    }),
  [DateRanges.THISWEEKANDLASTWEEK]: () =>
    fixed({
      endDate: yesterdayLocalAsISODate(),
      startDate: getStartOfWeek().subtract(1, "w").format("YYYY-MM-DD"),
    }),
  [DateRanges.THISMONTH]: () =>
    fixed({
      endDate: yesterdayLocalAsISODate(),
      startDate: getStartOfMonth().format("YYYY-MM-DD"),
    }),
  [DateRanges.THISMONTHANDLASTMONTH]: () =>
    fixed({
      endDate: yesterdayLocalAsISODate(),
      startDate: getStartOfMonth().subtract(1, "w").format("YYYY-MM-DD"),
    }),
  [DateRanges.MONTHTODATE]: () => {
    const anchor = yesterdayLocalAsISODate();
    return fixed({
      endDate: yesterdayLocalAsISODate(),
      startDate: anchor.substr(0, 8) + "01",
    });
  },
  [DateRanges.LASTMONTH]: () => {
    const lastMonthEnd = getStartOfMonth()
      .subtract(1, "d")
      .format("YYYY-MM-DD");
    return fixed({
      startDate: moment(lastMonthEnd, "YYYY-MM-DD")
        .startOf("month")
        .format("YYYY-MM-DD"),
      endDate: lastMonthEnd,
    });
  },
  [DateRanges.YEARTODATE]: () =>
    fixed({
      startDate: getStartOfYear().format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LASTYEAR]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(11, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST3YEARS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(35, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
  [DateRanges.LAST5YEARS]: () =>
    fixed({
      startDate: getStartOfMonth().subtract(59, "M").format("YYYY-MM-DD"),
      endDate: yesterdayLocalAsISODate(),
    }),
};

// TODO: Look into fixing inconsistencies between "yesterdayAndToday" and "todayAndYesterday"
export function dateRangeToInterval(
  dateRange: DateRange | "yesterdayAndToday",
): TypedDateInterval {
  const v = DateRangeIntervalConversions[dateRange];
  return typeof v === "function" ? v() : v;
}

// TODO: Use a ZOD Schema!
export function parseMonthInterval(arg: string | null): MonthInterval | null {
  if (!arg) {
    return null;
  }

  try {
    const rv = JSON.parse(decodeURIComponent(arg));
    if (!isMonthInterval(rv)) {
      return null;
    }
    return rv;
  } catch (e) {
    return null;
  }
}

// TODO: Use a ZOD Schema!
function isMonthInterval(arg: any): arg is MonthInterval {
  if (!arg.startMonth || !moment(arg.startMonth, true).isValid()) {
    return false;
  }

  if (!arg.endMonth || !moment(arg.endMonth, true).isValid()) {
    return false;
  }

  if (arg.endMonth < arg.startMonth) {
    return false;
  }

  return true;
}

type ParseDateRangeQueryParamOptions = {
  readonly restrictTo?: readonly DateRange[];
};

type ParsedDateRange<O extends ParseDateRangeQueryParamOptions> = O extends {
  readonly restrictTo: readonly (infer Vi extends DateRange)[];
}
  ? Extract<DateRange, Vi>
  : DateRange;

type ParsedReturn<O extends ParseDateRangeQueryParamOptions> =
  | ParsedDateRange<O>
  | FixedDateInterval
  | null;

export const parseDateRangeQueryParam = <
  O extends ParseDateRangeQueryParamOptions,
>(
  value: string | null,
  options: O,
): ParsedReturn<O> => {
  if (!value) {
    return null;
  }
  let result: unknown;
  try {
    result = JSON.parse(decodeURIComponent(value));
  } catch (e) {
    return null;
  }
  if (DateRanges.contains(result)) {
    const range = DateRanges.getSafe(result, {});
    if (options.restrictTo) {
      return options.restrictTo.includes(range)
        ? (range as ParsedReturn<O>)
        : null;
    }
    return range as ParsedReturn<O>;
  } else if (isFixedDateInterval(result)) {
    return result;
  }
  return null;
};
